import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SFMenuOption } from 'sfui';
import { UserContext } from 'ui-smartforce-settings';
import { FooterButtonAction } from '../../FooterButtons/FooterButtons';
import { StateConfigContext } from '../../../../../Context/StateConfigContext';
import { IntegrationReportsContext } from '../../../../../Context/IntegrationReportsContext';
import { Field, ReportConfig, ReportValue } from '../../../../../Models';
import { isIncidentNumberValid } from '../../../../../Helpers';
import PendingReportsService from '../../../../../Services/PendingReportsService';

/**
 * Extracts the case number field config from the report state config.
 */
export const getCaseNumberConfig = (
  stateConfig: ReportConfig | undefined
): Field | undefined => {
  if (!stateConfig) return undefined;
  const section = stateConfig.general_information.sections.find(
    (s) => s.name === 'to_connect_reports'
  );

  if (section) {
    return section.fields.find((f) => f.name === 'event_identifier');
  }

  return undefined;
};

/**
 * Generates a unique incident number from officer ID and timestamp.
 */
export const getIncidentNumberGenerated = (officerId: string): string => {
  const timestamp: string = new Date().getTime().toString();
  const officerIdWithoutSpecial: string = officerId.replace(
    /[^A-Za-z0-9-]/g,
    ''
  );

  const result = `${timestamp.substring(0, 10)}-${officerIdWithoutSpecial}`;
  // Ensure the incident number does not exceed 20 characters
  return result.substring(0, 20);
};

export interface UseIncidentNumberViewParams {
  onContinue: (
    incidentNumber: string,
    caseNumber: string,
    report?: ReportValue
  ) => void;
  onCleanUp: (
    incidentNumber: string,
    caseNumber: string,
    report?: ReportValue
  ) => void;
}

export interface UseIncidentNumberViewResult {
  incidentNumber: string;
  caseNumber: string;
  selectedReport: ReportValue | undefined;
  caseNumberConfig: ReturnType<typeof getCaseNumberConfig>;
  caseNumberOptions: SFMenuOption[];
  onCaseNumberChange: (value: string | SFMenuOption) => void;
  continueButton: FooterButtonAction;
}

export const useIncidentNumberView = ({
  onContinue,
  onCleanUp
}: UseIncidentNumberViewParams): UseIncidentNumberViewResult => {
  const { user } = useContext(UserContext);
  const stateConfig = useContext(StateConfigContext).stateConfig;
  const { integrationReports } = useContext(IntegrationReportsContext);

  const [pendingReports, setPendingReports] = useState<ReportValue[]>([]);
  const incidentNumberRef = useRef<string>(
    getIncidentNumberGenerated(user?.officer_id ?? '')
  );
  const [incidentNumber, setIncidentNumber] = useState(
    incidentNumberRef.current
  );
  const [selectedReport, setSelectedReport] = useState<ReportValue>();
  const [caseNumber, setCaseNumber] = useState<string>('');

  const caseNumberConfig = getCaseNumberConfig(stateConfig);

  const caseNumberOptions: SFMenuOption[] = integrationReports
    .filter((report) => {
      const isUsed = pendingReports.some((pendingReport) => {
        return (
          pendingReport.general_information.state_related?.to_connect_reports
            ?.event_identifier ===
          report.general_information.state_related?.to_connect_reports
            ?.event_identifier
        );
      });
      return !isUsed;
    })
    .map((report) => ({
      label: report.general_information.state_related.to_connect_reports
        .event_identifier as string,
      value: report.general_information.incident_number as string
    }));

  const onCaseNumberChange = useCallback(
    (value: string | SFMenuOption) => {
      if (typeof value === 'string') {
        setSelectedReport(undefined);
        setIncidentNumber(incidentNumberRef.current);
        setCaseNumber(value);
      } else {
        const reportSelected = integrationReports.find(
          (report) => report.general_information.incident_number === value.value
        );
        if (reportSelected) {
          setSelectedReport(reportSelected);
          setIncidentNumber(
            reportSelected.general_information.incident_number as string
          );
          setCaseNumber(value.label as string);
        }
      }
    },
    [integrationReports]
  );

  useEffect(() => {
    const loadPendingReports = async () => {
      if (!user?.email) return;
      const reports = await PendingReportsService.getUserReports(user.email);
      setPendingReports(reports);
    };

    loadPendingReports();
  }, [user?.email]);

  useEffect(() => {
    return () => {
      onCleanUp(incidentNumber, caseNumber, selectedReport);
    };
  }, [incidentNumber, caseNumber, selectedReport, onCleanUp]);

  const continueButton: FooterButtonAction = {
    label: 'Continue',
    disabled:
      !isIncidentNumberValid(incidentNumber) ||
      (caseNumber.length > 0 && !isIncidentNumberValid(caseNumber)),
    onClick: () => onContinue(incidentNumber, caseNumber, selectedReport)
  };

  return {
    incidentNumber,
    caseNumber,
    selectedReport,
    caseNumberConfig,
    caseNumberOptions,
    onCaseNumberChange,
    continueButton
  };
};
